/**
 * 内置词条种子的索引与加载器（bundled-user.ts、bundled-admin.ts 共用；本模块不引用任何种子文件）。
 * 种子文件路径形如 `../locales/<lang>/<namespace>.json`，每个语言 × 命名空间是一个单独的动态 import。
 */
import type { Lang } from './langs.js';
import type { Messages, NamespacedLoader } from './runtime.js';

export type SeedImporters = Readonly<Record<string, () => Promise<Messages>>>;

const SEED_PATH = /\/locales\/([a-z]{2})\/([A-Za-z0-9_-]+)\.json$/;

export interface SeedIndex {
  readonly files: ReadonlyMap<string, () => Promise<Messages>>; // `${lang}/${ns}`
  readonly namespaces: readonly string[];
}

/** 命名空间排序：common、admin 在前，其余按字母序。 */
function namespaceOrder(a: string, b: string): number {
  const rank = (ns: string) => (ns === 'common' ? 0 : ns === 'admin' ? 1 : 2);
  return rank(a) - rank(b) || a.localeCompare(b);
}

export function indexSeeds(...groups: SeedImporters[]): SeedIndex {
  const files = new Map<string, () => Promise<Messages>>();
  const namespaces = new Set<string>();
  for (const group of groups) {
    for (const [path, load] of Object.entries(group)) {
      const match = SEED_PATH.exec(path);
      if (!match) continue;
      const [, lang, ns] = match as unknown as [string, string, string];
      files.set(`${lang}/${ns}`, load);
      namespaces.add(ns);
    }
  }
  return { files, namespaces: [...namespaces].sort(namespaceOrder) };
}

export function createSeedLoader(index: SeedIndex, filter?: (namespace: string, key: string) => boolean): NamespacedLoader {
  const loader = async (lang: Lang, namespace: string): Promise<Messages> => {
    const load = index.files.get(`${lang}/${namespace}`);
    if (!load) return {};
    const messages = await load();
    if (!filter) return messages;
    const out: Record<string, string> = {};
    for (const [key, value] of Object.entries(messages)) if (filter(namespace, key)) out[key] = value;
    return out;
  };
  return Object.assign(loader, { namespaces: index.namespaces });
}
